import { useState, useEffect, useMemo, useCallback } from 'react';
import { Activity, ActivityAction, EntityType } from '../../ActivityMenu/types';
import { ITEMS_PER_PAGE } from '../../ActivityMenu/styles/constants';
import { UseAuth } from '@/contexts/AuthContext';
import {
  postUserAuditLogWorkspace,
  UserAuditLogEntry,
  UserAuditLogResponse,
} from '@/apis/core/UserAuditLog/post/postAuditLogWorkspace';
import { PostUserResponse } from '@/apis/core/user/post/postUser';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import { getUserDisplayNameFromRecord } from '@/utils/userDisplayName';

/** Extract response/body from unknown error for Azure/API parsing */
function getResponseFromError(err: unknown): unknown {
  const e = err as { response?: unknown; body?: unknown };
  return e?.response ?? e?.body;
}

// Map audit log actions to ActivityAction types
const mapAuditLogAction = (action: string): ActivityAction => {
  // Normalize action to match enum values
  const normalizedAction = action.trim();

  // Direct mapping for exact matches
  const actionMap: Record<string, ActivityAction> = {
    Created: 'Created',
    Deleted: 'Deleted',
    Modified: 'Modified',
    Removed: 'Removed',
    Added: 'Added',
    Opened: 'Opened',
    // Handle common variations
    Create: 'Created',
    Delete: 'Deleted',
    Modify: 'Modified',
    Remove: 'Removed',
    Add: 'Added',
    Open: 'Opened',
    Update: 'Modified',
    Edit: 'Modified',
  };

  // Check for exact match or normalized match
  if (actionMap[normalizedAction]) {
    return actionMap[normalizedAction];
  }

  // Default to Created for unmapped actions
  return 'Created';
};

// Transform audit log entry to Activity
const transformAuditLogToActivity = (
  log: UserAuditLogEntry,
  index: number,
  displayName: string,
  userId?: string
): Activity => {
  const action = mapAuditLogAction(log.action);
  const entityType = log.entityType as EntityType;
  const details = `${log.action} ${log.entityType}${
    log.entityName ? `: ${log.entityName}` : ''
  }`;

  // Create a unique ID combining timestamp, entityId, and index to ensure uniqueness
  const uniqueId = `${log.date}-${log.entityId || 'unknown'}-${index}`;

  return {
    id: uniqueId,
    timestamp: log.date,
    action,
    entityType,
    details,
    displayName: displayName || 'System',
    userId: userId ?? log.userId,
  };
};

export interface WorkspaceUserOption {
  id: string;
  displayName: string;
}

export interface UseAuditLogActivitiesProps {
  startDate?: Date;
  endDate?: Date;
  fetchActivities?: boolean;
  fetchTodayCount?: boolean;
  /** Workspace user data (id -> user) for filtering and API users list */
  workspaceUserData?: Record<string, PostUserResponse>;
  workspaceId?: string;
}

export const useAuditLogActivities = (props?: UseAuditLogActivitiesProps) => {
  const { currentUser, getAccessToken } = UseAuth();
  const { handleError } = useWorkspaceErrorHandler(props?.workspaceId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [allActivities, setAllActivities] = useState<Activity[]>([]);
  const [totalActivitiesToday, setTotalActivitiesToday] = useState(0);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [activityFilter, setActivityFilter] = useState<ActivityAction | 'all'>(
    'all'
  );
  const [typeFilter, setTypeFilter] = useState<EntityType | 'all'>('all');
  const [userFilter, setUserFilter] = useState<string | 'all'>('all');

  // Workspace users for filter dropdown (id + display name)
  const workspaceUserOptions = useMemo((): WorkspaceUserOption[] => {
    const userData = props?.workspaceUserData;
    if (!userData) return [];
    return Object.entries(userData).map(([id, user]) => ({
      id,
      displayName: getUserDisplayNameFromRecord(user),
    }));
  }, [props?.workspaceUserData]);

  // Memoize filtered activities to prevent unnecessary recalculations
  const filteredActivities = useMemo(() => {
    let filtered = [...allActivities]; // Create a copy to avoid mutating the original

    // Filter by action
    if (activityFilter !== 'all') {
      filtered = filtered.filter((a) => {
        const matches = a.action === activityFilter;
        return matches;
      });
    }

    // Filter by entity type
    if (typeFilter !== 'all') {
      filtered = filtered.filter((a) => a.entityType === typeFilter);
    }

    // Filter by user
    if (userFilter !== 'all') {
      filtered = filtered.filter((a) => a.userId === userFilter);
    }

    // Sort by most recent first (newest timestamp first)
    filtered.sort((a, b) => {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });

    return filtered;
  }, [allActivities, activityFilter, typeFilter, userFilter]);

  // Memoize visible activities
  const visibleActivities = useMemo(
    () => filteredActivities.slice(0, visibleCount),
    [filteredActivities, visibleCount]
  );

  // Memoize hasMore
  const hasMore = useMemo(
    () => visibleCount < filteredActivities.length,
    [filteredActivities.length, visibleCount]
  );

  // Reset visible count when filter changes
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [activityFilter, typeFilter, userFilter]);

  // Fetch activities for the specified date range (only when fetchActivities is true)
  useEffect(() => {
    if (!currentUser || props?.fetchActivities !== true) {
      return;
    }

    const fetchAuditLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        // Determine date range - use props if provided, otherwise default to last 30 days
        const endDate = props?.endDate || new Date();
        const startDate =
          props?.startDate ||
          (() => {
            const date = new Date();
            date.setDate(date.getDate() - 30);
            return date;
          })();

        // Workspace user IDs to request from API
        const workspaceUserIds = props?.workspaceUserData
          ? Object.keys(props.workspaceUserData)
          : [];

        const response = await postUserAuditLogWorkspace({
          getAccessToken,
          workspaceId: props?.workspaceId || '',
          body: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            results: '10000', // Fetch more to allow pagination
            offset: '0',
            users: workspaceUserIds,
          },
        });

        // Normalize: API may return single response or array of responses
        const responses: UserAuditLogResponse[] = Array.isArray(response)
          ? response
          : [response];

        const userData = props?.workspaceUserData ?? {};
        const getDisplayNameForUserId = (userId: string): string => {
          const u = userData[userId];
          return getUserDisplayNameFromRecord(u);
        };

        const activities: Activity[] = [];
        responses.forEach((res) => {
          res.logs?.forEach((log) => {
            const logUserId = log.userId ?? res.userId;
            activities.push(
              transformAuditLogToActivity(
                log,
                activities.length,
                getDisplayNameForUserId(logUserId),
                logUserId
              )
            );
          });
        });

        // Sort activities by most recent first (newest timestamp first)
        activities.sort((a, b) => {
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        });

        setAllActivities(activities);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to load audit logs');
        const response = getResponseFromError(err);

        console.error('Failed to load audit logs:', error);
        setError(error);
        setAllActivities([]);
        handleError(error, 'Failed to load audit logs', response);
      } finally {
        setLoading(false);
      }
    };

    void fetchAuditLogs();
  }, [
    getAccessToken,
    currentUser,
    handleError,
    props?.startDate,
    props?.endDate,
    props?.workspaceId,
    props?.workspaceUserData,
  ]);

  // Fetch today's activities count (only when fetchTodayCount is true)
  useEffect(() => {
    if (!currentUser || props?.fetchTodayCount !== true) {
      return;
    }

    const fetchTodayActivities = async () => {
      try {
        // Get today's date range (start of today to now)
        const today = new Date();
        const startOfToday = new Date(today);
        startOfToday.setHours(0, 0, 0, 0);
        const endOfToday = new Date(today);
        endOfToday.setHours(23, 59, 59, 999);

        const workspaceUserIds = props?.workspaceUserData
          ? Object.keys(props.workspaceUserData)
          : [];

        const response = await postUserAuditLogWorkspace({
          getAccessToken,
          workspaceId: props?.workspaceId || '',
          body: {
            startDate: startOfToday.toISOString(),
            endDate: endOfToday.toISOString(),
            results: '10000',
            offset: '0',
            users: workspaceUserIds,
          },
        });

        // Ensure response is fully resolved before accessing properties
        if (!response) {
          console.warn("Today's activities response is null or undefined");
          setTotalActivitiesToday(0);
          return;
        }

        const responses: UserAuditLogResponse[] = Array.isArray(response)
          ? response
          : [response];
        const allLogs = responses.flatMap((res) => res.logs ?? []);

        // Filter to ensure we only count activities from today
        const todayActivities = allLogs.filter((log) => {
          if (!log || !log.date) return false;
          const logDate = new Date(log.date);
          const logTime = logDate.getTime();
          const startTime = startOfToday.getTime();
          const endTime = endOfToday.getTime();
          return logTime >= startTime && logTime <= endTime;
        });

        setTotalActivitiesToday(todayActivities.length);
      } catch (err) {
        // Non-critical: optional count; log only, safe fallback
        console.error("Failed to get today's activities count:", err);
        setTotalActivitiesToday(0);
      }
    };

    void fetchTodayActivities();
  }, [
    getAccessToken,
    currentUser,
    props?.fetchTodayCount,
    props?.workspaceUserData,
  ]);

  // Fetch more activities
  const fetchMoreActivities = useCallback(() => {
    if (isLoadingMore || !hasMore) return Promise.resolve();
    setIsLoadingMore(true);

    return new Promise<void>((resolve) => {
      setTimeout(() => {
        setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
        setIsLoadingMore(false);
        resolve();
      }, 300);
    });
  }, [isLoadingMore, hasMore]);

  return {
    loading,
    error,
    visibleActivities,
    isLoadingMore,
    hasMore,
    activityFilter,
    setActivityFilter,
    typeFilter,
    setTypeFilter,
    userFilter,
    setUserFilter,
    workspaceUserOptions,
    fetchMoreActivities,
    totalActivities: filteredActivities.length,
    totalActivitiesToday,
  };
};
